import React, { useEffect, useState } from 'react';
import { Card, Form, Table } from 'react-bootstrap-v5';
import Select from 'react-select';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getRequestOptions } from '../helpers/Fetchwrapper';
import { numberFormat } from '../helpers/GlobalHelper';
import { useLocation } from 'react-router-dom';

interface ProjectOption {
	label: string;
	value: string | number;
}

interface SkuRow {
	name: string;
	achievedLtr: number;
	amount: number;
}

interface SellerRow {
	name: string;
	target: number;
	achievedTarget: number;
	achievedLtrPercent: number;
}

interface DetailRow {
	name: string;
	targetLiter: number;
	achievedLtr: number;
	amount: number;
	achievedLtrPercent: number;
}

interface MisDashboardData {
	title: string;
	periodLabel: string;
	totalSales: {
		totalTarget: number;
		achievedTarget: number;
		totalAmount: number;
		achievedPercent: number;
	};
	oldCustomer: number;
	newCustomer: number;
	mostSellingItem: {
		name: string;
		quantity: number;
	};
	numberOfInvoice: number;
	bestCustomer: {
		name: string;
		value: number;
		location: string;
	};
	metrics: {
		totalTarget: number;
		achievedTarget: number;
		achievedTargetPercent: number;
		multigradeTarget: number;
		multigradeAchieved: number;
		multigradeAchievedPercent: number;
		mcoBestDistrict: {
			name: string;
			quantity: number;
			unit: string;
		};
		cngBestDistrict: {
			name: string;
			quantity: number;
			unit: string;
		};
	};
	skuList: SkuRow[];
	bestDivisionForMco: {
		name: string;
		quantity: number;
		unit: string;
	};
	bestSellers: SellerRow[];
	details: DetailRow[];
	totalFreeProducts: number;
	numberOfCustomersTrans: number;
}

const getDefaultMonthRange = () => {
	const now = new Date();
	const month = String(now.getMonth() + 1).padStart(2, '0');
	const currentMonth = `${now.getFullYear()}-${month}`;

	return {
		fromDate: currentMonth,
		toDate: currentMonth,
	};
};

const formatMonthLabel = (fromDate: string, toDate: string) => {
	const formatOne = (value: string) => {
		if (!value) return '';
		const [year, month] = value.split('-');
		const date = new Date(Number(year), Number(month) - 1, 1);
		return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
	};

	if (fromDate === toDate) {
		return formatOne(fromDate);
	}

	return `${formatOne(fromDate)} - ${formatOne(toDate)}`;
};

const buildPlaceholderData = (projectName: string, fromDate: string, toDate: string): MisDashboardData => ({
	title: `${projectName || 'Project'} Dashboard`,
	periodLabel: formatMonthLabel(fromDate, toDate),
	totalSales: {
		totalTarget: 0,
		achievedTarget: 0,
		totalAmount: 0,
		achievedPercent: 0,
	},
	oldCustomer: 0,
	newCustomer: 0,
	mostSellingItem: {
		name: '-',
		quantity: 0,
	},
	numberOfInvoice: 0,
	bestCustomer: {
		name: '-',
		value: 0,
		location: '-',
	},
	metrics: {
		totalTarget: 0,
		achievedTarget: 0,
		achievedTargetPercent: 0,
		multigradeTarget: 0,
		multigradeAchieved: 0,
		multigradeAchievedPercent: 0,
		mcoBestDistrict: {
			name: '-',
			quantity: 0,
			unit: 'Ltr.',
		},
		cngBestDistrict: {
			name: '-',
			quantity: 0,
			unit: 'Ltr.',
		},
	},
	skuList: [],
	bestDivisionForMco: {
		name: '-',
		quantity: 0,
		unit: 'Ltr.',
	},
	bestSellers: [],
	details: [],
	totalFreeProducts: 0,
	numberOfCustomersTrans: 0,
});

const boxStyle: React.CSSProperties = {
	border: '1px solid #E4E6EF',
	borderRadius: 4,
	background: '#fff',
	height: '100%',
	overflow: 'hidden',
};

const boxHeaderStyle: React.CSSProperties = {
	background: '#F5F8FA',
	borderBottom: '1px solid #E4E6EF',
	padding: '8px 12px',
	fontWeight: 700,
	textAlign: 'center',
};

const boxBodyStyle: React.CSSProperties = {
	padding: '12px',
};

const MisDashboardReport: React.FC = () => {
	const location = useLocation();
	const query = new URLSearchParams(location.search);
	const defaultRange = getDefaultMonthRange();

	const [loading, setLoading] = useState(false);
	const [showReport, setShowReport] = useState(false);
	const [reportData, setReportData] = useState<MisDashboardData | null>(null);
	const [projects, setProjects] = useState<ProjectOption[]>([]);
	const [project, setProject] = useState<ProjectOption>({ label: 'Select Project', value: '' });
	const [formData, setFormData] = useState({
		idProjects: query.get('idProjects') ?? '',
		fromDate: query.get('fromDate') ?? defaultRange.fromDate,
		toDate: query.get('toDate') ?? defaultRange.toDate,
	});

	useEffect(() => {
		getProjects();
	}, []);

	useEffect(() => {
		if (projects.length > 0) {
			const projectObj = Number(query.get('idProjects'))
				? projects.find((item) => String(item.value) === String(query.get('idProjects')))
				: projects[0];

			setProject({
				label: projectObj?.label ?? 'Select Project',
				value: projectObj?.value ?? '',
			});
			setFormData((prev) => ({
				...prev,
				idProjects: projectObj ? String(projectObj.value) : '',
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [projects]);

	const getProjects = () => {
		fetch(process.env.REACT_APP_API_URL + 'lead/projects', getRequestOptions())
			.then((resp) => resp.json())
			.then((resp) => {
				const projectOptions = (resp.data || []).map((item: any) => ({
					label: item.name,
					value: item.id,
				}));
				setProjects(projectOptions);
			})
			.catch((error) => console.error('Error fetching projects:', error));
	};

	const handleProjectChange = (selected: any) => {
		setProject(selected || { label: 'Select Project', value: '' });
		setFormData((prev) => ({
			...prev,
			idProjects: selected ? String(selected.value) : '',
		}));
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleViewReport = () => {
		if (!formData.idProjects) {
			toast.error('Please select a project', { position: 'bottom-right', autoClose: 4000 });
			return;
		}
		if (!formData.fromDate || !formData.toDate) {
			toast.error('Please select both From Month and To Month', { position: 'bottom-right', autoClose: 4000 });
			return;
		}
		if (formData.fromDate > formData.toDate) {
			toast.error('To Month must be after or equal to From Month', { position: 'bottom-right', autoClose: 4000 });
			return;
		}

		setLoading(true);
		const data = buildPlaceholderData(project.label, formData.fromDate, formData.toDate);
		setReportData(data);
		setShowReport(true);
		setLoading(false);
	};

	const skuTotalAchieved = reportData?.skuList.reduce((sum, row) => sum + row.achievedLtr, 0) ?? 0;
	const skuTotalAmount = reportData?.skuList.reduce((sum, row) => sum + row.amount, 0) ?? 0;

	return (
		<>
			<Card className="mb-4">
				<Card.Body>
					<div className="row">
						<div className="col-md-3">
							<Form.Group className="mb-3">
								<Form.Label>
									Project<span className="required"></span>
								</Form.Label>
								<Select
									value={project}
									onChange={handleProjectChange}
									options={projects}
									menuPosition="fixed"
									styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
									isClearable={true}
								/>
							</Form.Group>
						</div>
						<div className="col-md-2">
							<Form.Group className="mb-3">
								<Form.Label>
									From Month<span className="required"></span>
								</Form.Label>
								<Form.Control
									type="month"
									name="fromDate"
									value={formData.fromDate}
									onChange={handleChange}
								/>
							</Form.Group>
						</div>
						<div className="col-md-2">
							<Form.Group className="mb-3">
								<Form.Label>
									To Month<span className="required"></span>
								</Form.Label>
								<Form.Control
									type="month"
									name="toDate"
									value={formData.toDate}
									onChange={handleChange}
								/>
							</Form.Group>
						</div>
						<div className="col-md-2">
							<div className="mt-8">
								<button className="btn btn-sm btn-primary" onClick={handleViewReport} disabled={loading}>
									{loading && <span className="spinner-border spinner-border-sm me-1"></span>}
									<span>
										<i className="fas fa-eye"></i> View Report
									</span>
								</button>
							</div>
						</div>
					</div>
				</Card.Body>
			</Card>

			{showReport && reportData && (
				<Card>
					<Card.Body>
						<div className="text-center mb-5">
							<h3 className="fw-bolder mb-1">{reportData.title}</h3>
							<div className="text-muted fw-bold">{reportData.periodLabel}</div>
						</div>

						{/* Row 1 */}
						<div className="row mb-4">
							<div className="col-lg-3 col-md-6 mb-3">
								<div style={boxStyle}>
									<div style={boxHeaderStyle}>Total Sales</div>
									<div style={boxBodyStyle}>
										<table className="table table-sm table-borderless mb-0">
											<tbody>
												<tr>
													<td>Total Target</td>
													<td className="text-end fw-bold">{numberFormat(reportData.totalSales.totalTarget)}</td>
												</tr>
												<tr>
													<td>Achieved Target</td>
													<td className="text-end fw-bold">{numberFormat(reportData.totalSales.achievedTarget)}</td>
												</tr>
												<tr>
													<td>Total Amount</td>
													<td className="text-end fw-bold">{numberFormat(reportData.totalSales.totalAmount)}</td>
												</tr>
												<tr>
													<td>Achieved %</td>
													<td className="text-end fw-bold">{numberFormat(reportData.totalSales.achievedPercent)}</td>
												</tr>
											</tbody>
										</table>
									</div>
								</div>
							</div>

							<div className="col-lg-2 col-md-6 mb-3">
								<div className="mb-3" style={boxStyle}>
									<div style={boxHeaderStyle}>Old Customer</div>
									<div style={{ ...boxBodyStyle, textAlign: 'center', fontWeight: 700, fontSize: '1.75rem' }}>
										{numberFormat(reportData.oldCustomer)}
									</div>
								</div>
								<div style={boxStyle}>
									<div style={boxHeaderStyle}>New Customer</div>
									<div style={{ ...boxBodyStyle, textAlign: 'center', fontWeight: 700, fontSize: '1.75rem' }}>
										{numberFormat(reportData.newCustomer)}
									</div>
								</div>
							</div>

							<div className="col-lg-3 col-md-6 mb-3">
								<div style={boxStyle}>
									<div style={boxHeaderStyle}>Most Selling Items</div>
									<div style={{ ...boxBodyStyle, textAlign: 'center' }}>
										<div className="fw-bold mb-3">{reportData.mostSellingItem.name}</div>
										<div
											style={{
												border: '1px solid #E4E6EF',
												borderRadius: 4,
												padding: '20px 10px',
												fontWeight: 700,
												fontSize: '1.75rem',
											}}
										>
											{numberFormat(reportData.mostSellingItem.quantity)}
										</div>
									</div>
								</div>
							</div>

							<div className="col-lg-2 col-md-6 mb-3">
								<div style={boxStyle}>
									<div style={boxHeaderStyle}>Number of Invoice</div>
									<div style={{ ...boxBodyStyle, textAlign: 'center', fontWeight: 700, fontSize: '1.75rem', paddingTop: 40, paddingBottom: 40 }}>
										{numberFormat(reportData.numberOfInvoice)} Pcs
									</div>
								</div>
							</div>

							<div className="col-lg-2 col-md-6 mb-3">
								<div style={boxStyle}>
									<div style={boxHeaderStyle}>Best Customer</div>
									<div style={{ ...boxBodyStyle, textAlign: 'center' }}>
										<div className="fw-bold mb-2">{reportData.bestCustomer.name}</div>
										<div className="fw-bolder mb-2" style={{ fontSize: '1.5rem' }}>
											{numberFormat(reportData.bestCustomer.value)}
										</div>
										<div className="text-muted">{reportData.bestCustomer.location}</div>
									</div>
								</div>
							</div>
						</div>

						{/* Row 2 */}
						<div className="row mb-4">
							<div className="col-lg-2 col-md-4 col-sm-6 mb-3">
								<div style={boxStyle}>
									<div style={boxHeaderStyle}>Total Target</div>
									<div style={{ ...boxBodyStyle, textAlign: 'center', fontWeight: 700, fontSize: '1.25rem' }}>
										{numberFormat(reportData.metrics.totalTarget)}
									</div>
								</div>
							</div>
							<div className="col-lg-2 col-md-4 col-sm-6 mb-3">
								<div style={boxStyle}>
									<div style={boxHeaderStyle}>Achieved Target</div>
									<div style={{ ...boxBodyStyle, textAlign: 'center' }}>
										<div className="fw-bolder" style={{ fontSize: '1.25rem' }}>
											{numberFormat(reportData.metrics.achievedTarget)}
										</div>
										<div className="text-muted fw-bold">{numberFormat(reportData.metrics.achievedTargetPercent)}%</div>
									</div>
								</div>
							</div>
							<div className="col-lg-2 col-md-4 col-sm-6 mb-3">
								<div style={boxStyle}>
									<div style={boxHeaderStyle}>Multigrade Target</div>
									<div style={{ ...boxBodyStyle, textAlign: 'center', fontWeight: 700, fontSize: '1.25rem' }}>
										{numberFormat(reportData.metrics.multigradeTarget)}
									</div>
								</div>
							</div>
							<div className="col-lg-2 col-md-4 col-sm-6 mb-3">
								<div style={boxStyle}>
									<div style={boxHeaderStyle}>Achieved Target</div>
									<div style={{ ...boxBodyStyle, textAlign: 'center' }}>
										<div className="fw-bolder" style={{ fontSize: '1.25rem' }}>
											{numberFormat(reportData.metrics.multigradeAchieved)}
										</div>
										<div className="text-muted fw-bold">{numberFormat(reportData.metrics.multigradeAchievedPercent)}%</div>
									</div>
								</div>
							</div>
							<div className="col-lg-2 col-md-4 col-sm-6 mb-3">
								<div style={boxStyle}>
									<div style={boxHeaderStyle}>MCO Best District</div>
									<div style={{ ...boxBodyStyle, textAlign: 'center' }}>
										<div className="fw-bold">{reportData.metrics.mcoBestDistrict.name}</div>
										<div className="fw-bolder">
											{numberFormat(reportData.metrics.mcoBestDistrict.quantity)} {reportData.metrics.mcoBestDistrict.unit}
										</div>
									</div>
								</div>
							</div>
							<div className="col-lg-2 col-md-4 col-sm-6 mb-3">
								<div style={boxStyle}>
									<div style={boxHeaderStyle}>CNG Best District</div>
									<div style={{ ...boxBodyStyle, textAlign: 'center' }}>
										<div className="fw-bold">{reportData.metrics.cngBestDistrict.name}</div>
										<div className="fw-bolder">
											{numberFormat(reportData.metrics.cngBestDistrict.quantity)} {reportData.metrics.cngBestDistrict.unit}
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Row 3 */}
						<div className="row mb-4">
							<div className="col-lg-5 mb-3">
								<div style={boxStyle}>
									<div className="table-responsive">
										<Table bordered hover size="sm" className="mb-0 align-middle">
											<thead>
												<tr className="fw-bolder" style={{ background: '#F5F8FA' }}>
													<th>SKU List</th>
													<th className="text-end">Achieved Ltr.</th>
													<th className="text-end">Amount</th>
												</tr>
											</thead>
											<tbody>
												{reportData.skuList.length === 0 ? (
													<tr>
														<td colSpan={3} className="text-center text-muted py-5">
															No SKU data
														</td>
													</tr>
												) : (
													reportData.skuList.map((row) => (
														<tr key={row.name}>
															<td>{row.name}</td>
															<td className="text-end">{numberFormat(row.achievedLtr)}</td>
															<td className="text-end">{numberFormat(row.amount)}</td>
														</tr>
													))
												)}
												<tr>
													<td className="fw-bolder">Total</td>
													<td className="text-end fw-bolder">{numberFormat(skuTotalAchieved)}</td>
													<td className="text-end fw-bolder">{numberFormat(skuTotalAmount)}</td>
												</tr>
											</tbody>
										</Table>
									</div>
								</div>
							</div>

							<div className="col-lg-2 mb-3">
								<div style={boxStyle}>
									<div style={boxHeaderStyle}>Best Division For MCO</div>
									<div style={{ ...boxBodyStyle, textAlign: 'center', minHeight: 160, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
										<div className="fw-bold mb-3">{reportData.bestDivisionForMco.name}</div>
										<div className="fw-bolder" style={{ fontSize: '1.75rem' }}>
											{numberFormat(reportData.bestDivisionForMco.quantity)} {reportData.bestDivisionForMco.unit}
										</div>
									</div>
								</div>
							</div>

							<div className="col-lg-5 mb-3">
								<div style={boxStyle}>
									<div style={boxHeaderStyle}>Best Seller Nationally (Multigrade)</div>
									<div className="table-responsive">
										<Table bordered hover size="sm" className="mb-0 align-middle">
											<thead>
												<tr className="fw-bolder" style={{ background: '#F5F8FA' }}>
													<th>Name</th>
													<th className="text-end">Target</th>
													<th className="text-end">Achieved Target</th>
													<th className="text-end">Achieved Ltr. %</th>
												</tr>
											</thead>
											<tbody>
												{reportData.bestSellers.length === 0 ? (
													<tr>
														<td colSpan={4} className="text-center text-muted py-5">
															No seller data
														</td>
													</tr>
												) : (
													reportData.bestSellers.map((row) => (
														<tr key={row.name}>
															<td>{row.name}</td>
															<td className="text-end">{numberFormat(row.target)}</td>
															<td className="text-end">{numberFormat(row.achievedTarget)}</td>
															<td className="text-end">{numberFormat(row.achievedLtrPercent)}</td>
														</tr>
													))
												)}
											</tbody>
										</Table>
									</div>
								</div>
							</div>
						</div>

						{/* Row 4 */}
						<div className="row">
							<div className="col-lg-9 mb-3">
								<div style={boxStyle}>
									<div className="table-responsive">
										<Table bordered hover size="sm" className="mb-0 align-middle">
											<thead>
												<tr className="fw-bolder" style={{ background: '#F5F8FA' }}>
													<th>Details</th>
													<th className="text-end">Target Liter</th>
													<th className="text-end">Achieved Ltr.</th>
													<th className="text-end">Amount</th>
													<th className="text-end">Achieved Ltr. %</th>
												</tr>
											</thead>
											<tbody>
												{reportData.details.length === 0 ? (
													<tr>
														<td colSpan={5} className="text-center text-muted py-8">
															No details data
														</td>
													</tr>
												) : (
													reportData.details.map((row) => (
														<tr key={row.name}>
															<td>{row.name}</td>
															<td className="text-end">{numberFormat(row.targetLiter)}</td>
															<td className="text-end">{numberFormat(row.achievedLtr)}</td>
															<td className="text-end">{numberFormat(row.amount)}</td>
															<td className="text-end">{numberFormat(row.achievedLtrPercent)}</td>
														</tr>
													))
												)}
											</tbody>
										</Table>
									</div>
								</div>
							</div>

							<div className="col-lg-3 mb-3">
								<div className="mb-3" style={boxStyle}>
									<div style={boxHeaderStyle}>Total Free Products</div>
									<div style={{ ...boxBodyStyle, textAlign: 'center', fontWeight: 700, fontSize: '1.75rem', paddingTop: 36, paddingBottom: 36 }}>
										{numberFormat(reportData.totalFreeProducts)}
									</div>
								</div>
								<div style={boxStyle}>
									<div style={boxHeaderStyle}>Number Of Customers Trans.</div>
									<div style={{ ...boxBodyStyle, textAlign: 'center', fontWeight: 700, fontSize: '1.75rem', paddingTop: 36, paddingBottom: 36 }}>
										{numberFormat(reportData.numberOfCustomersTrans)}
									</div>
								</div>
							</div>
						</div>
					</Card.Body>
				</Card>
			)}
		</>
	);
};

export default MisDashboardReport;
